import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, TextField, Button, Autocomplete, styled as styles, useTheme, CircularProgress, Link, Snackbar, Alert } from '@mui/material'
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as AskChatIcon  } from '../../assets/icons/icon_ask_chat.svg'
import {  AppLogo } from '../../AppLogo'
import { styled } from "../../theme";
import maskPhoneInput from '../../helpers/maskPhoneInput';
import formatPhoneNumber from '../../helpers/formatPhoneNumber';
import { getRegions, postRegister, checkAvailability } from '../../network/register';
import { addRegions } from '../../reducers/Ref';
import Cookies from 'js-cookie';
import { validationRegisterData } from './validations';
import useValidate from '../../hooks/useValidate';


function Register() {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { regions } = useSelector(state => state.ref)
  const [stepOneValidation, setStepOneValidation] = useState({
    fio: '',
    email: ''
  })

  const [submitData, setSubmitData] = useState({
    // Данные пользователя
    fio:          null,
    email:        null,
    phone:        null,

    // Данные УК
    cnt_name:     null,
    inn:          null,
    region_code:  null,

    // Для битрикса
    region_name:  null,

    // Шаг регистрации (не отправляется, просто для фронтенда)
    step:         1
  });

  // Обработка состояния запроса
  const [loadingStatus, setLoadingStatus] = useState({
    isLoading:        false,
    error:            false,
    errorMessage:     null
  });

  // Валидация формы
  const { validate, errors } = useValidate({ 
    schema: validationRegisterData, 
    data: {
      ...submitData, 
      cnt_name: submitData.cnt_name ? String(submitData.cnt_name).trim()  : undefined, 
      fio:      submitData.fio      ? String(submitData.fio).trim()       : undefined, 
      inn:      submitData.inn      ? String(submitData.inn).trim()       : undefined,
    }
   })

  // Форматирование телефона
  const sanitizePhone = (phone) => {
    let sanitizedPhone = phone;

    if (sanitizedPhone && sanitizedPhone.startsWith('+7')) {
      sanitizedPhone = sanitizedPhone.substring(2);
    }

    return sanitizedPhone
  }
  
  
  // Меняем название документа
  useEffect(() => {
    document.title = 'Регистрация в DS24'
  }, [])

  // Обработка ошибок
  useEffect(() => {
    if (loadingStatus.error) {
      const timeout = setTimeout(() => {
        setLoadingStatus(prev => ({ ...prev, error: false }))
      }, 3000)

      // Чистим из памяти при анмаунте
      return () => clearTimeout(timeout)
    }
  }, [loadingStatus.error])


  // Получение списка регионов
  const fetchRegions = async () => {
      setLoadingStatus({ isLoading: true, error: false }) 
      getRegions({},(err, resp) => {
        if(err) {
          setLoadingStatus({ isLoading: false, error: true })
          return;
        }
        dispatch(addRegions(resp))
        setLoadingStatus({ isLoading: false, error: false }) 
      });       
    }

    useEffect(() => {
      fetchRegions();
    },[]);

  const validateStepOne = async () => {
    let isValid = true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setStepOneValidation({ fio: '', email: '' })

    if(!submitData.fio || !submitData.fio.trim()) {
      setStepOneValidation(prev => ({...prev,  fio: 'Поле обязательное' }))
      isValid = false
    }  

    if(!submitData.email || !submitData.email.trim()) {
      setStepOneValidation(prev => ({...prev,  email: 'Поле обязательное' }))
      isValid = false
    } 

    if(!submitData.phone || !submitData.phone.trim()) {
      setStepOneValidation(prev => ({...prev,  phone: 'Поле обязательное' }))
      isValid = false
    }

    if(submitData.email && !emailRegex.test(submitData.email)) {
      setStepOneValidation(prev => ({...prev,  email: 'Недопустимое значение для поля email' }))
      isValid = false
    } 

    if(!isValid) return;

    // Запрос на бэк ауфа с проверкой телефона и почты
    if(submitData.email || submitData.phone) {
      const sanitizedPhone = sanitizePhone(submitData.phone);
  
      setLoadingStatus({ isLoading: true, error: false, errorMessage: null }) 
      const availability = await checkAvailability({ email: submitData.email, phone: sanitizedPhone });
  
      // Если выпали в ошибку
      if(!availability.ok) {
        setLoadingStatus({ isLoading: false, error: true, errorMessage: null })
        return;
      }
  
      // Если пользователь уже существует, то отдает текст ошибки
      if(availability.is_exist) {
        setLoadingStatus({ 
          isLoading: false, 
          error: true, 
          errorMessage: 'Пользователь с таким телефоном или почтой уже зарегистрирован' 
        })
      } else {
        // Двигаемся на шаг 2, если все хорошо
        /* global ym */
        ym(52608580, 'reachGoal', 'AuthRegistrationStep1NextClick');
        setLoadingStatus({ isLoading: false, error: null, errorMessage: null })
        setSubmitData((prevData) => ({ ...prevData, step: 2 }))
      }
    } else {
      setSubmitData((prevData) => ({ ...prevData, step: 2 }))
    }
  }


  const submit = async () => {
    if(validate()) {
      const sanitizedPhone = sanitizePhone(submitData.phone);

      // Получаем UTM-метки из query string
      const searchParams = new URLSearchParams(window.location.search);

      // Создаем объект для UTM-меток
      const bitrix_utms = {
        utm_campaign: searchParams.get('utm_campaign') || undefined,
        utm_content:  searchParams.get('utm_content')  || undefined,
        utm_medium:   searchParams.get('utm_medium')   || undefined,
        utm_source:   searchParams.get('utm_source')   || undefined,
        utm_term:     searchParams.get('utm_term')     || undefined
      };

      setLoadingStatus({ isLoading: true, error: null, errorMessage: null })
      postRegister({
        fio:            submitData.fio         ? submitData.fio         : undefined,
        email:          submitData.email       ? submitData.email       : undefined,
        phone:          sanitizedPhone         ? sanitizedPhone         : undefined,
        cnt_name:       submitData.cnt_name    ? submitData.cnt_name    : undefined,
        inn:            submitData.inn         ? submitData.inn         : undefined,
        region_code:    submitData.region_code ? submitData.region_code : undefined,
        region_name:    submitData.region_name ? submitData.region_name : undefined,
        bitrix_utms:    bitrix_utms
      }, (err, resp) => {
        if(err) {
          setLoadingStatus({ isLoading: false, error: true })
          return;
        } 
            setLoadingStatus({ isLoading: false, error: null }) 
            ym(52608580, 'reachGoal', 'AuthRegistrationStep2StartWorkClick');
            
            // При успешной регистрации редиректим
            Cookies.set('login',  resp.login.toUpperCase(), { expires: 365 });
            Cookies.set('hash',   resp.hash.toUpperCase(),  { expires: 365 });
            window.location.href = 'https://auth.ds24.ru/';
  
      })
    }

  }

  return (
    <Box 
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        bgcolor: '#EDEFF0'
      }}
    >
      <Card>
        <Grid pb={3.5}>
          <Box sx={{ pb: 3, '& svg': { width: 92, height: 'auto', cursor: 'default' } }}><AppLogo /></Box>  
          <Typography variant='h3' fontWeight={900} pb={2.5}>{submitData.step == 1 ? 'Регистрация' : 'Остался последний шаг'}</Typography> 
          <Typography lineHeight={1.25} sx={{ color: theme.palette.grey.main }}>
          {submitData.step == 1 
            ? 'Зарегистрируйтесь в системе сейчас и получите демодоступ к электронному журналу на 14 дней бесплатно.' 
            : 'Заполните данные о вашей организации, пока мы настраиваем ваш аккаунт и можно начинать работу!'
          }
          </Typography>    
        </Grid>

        <Grid container gap={3}>
          {/* Регистрация пользователя */}
          {submitData.step == 1 && (
            <>
            <Grid item xs={12} >
              <Grid container item spacing={3}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Телефон *</Typography>
                  <TextField fullWidth size="small"      
                    disabled={loadingStatus.isLoading} 
                    error={!!errors.phone || stepOneValidation.phone}
                    helperText={errors.phone || stepOneValidation.phone}
                    value={
                      (() => {
                          let phone = submitData.phone ?? '';
                          return formatPhoneNumber(phone);
                      })()
                    }
                    onChange={(e) => {
                      maskPhoneInput(
                        e.target.value,
                        '+7',
                        10,
                        (formattedPhone) => { setSubmitData((prevData) => ({ ...prevData, phone: formattedPhone }))}
                      );
                    }}
                    sx={{ ...styled('autocomplete',"input", "filled") }}         
                    placeholder="+7 (_ _ _) _ _ _ - _ _ - _ _" 
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} >
              <Grid container item spacing={3}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Почта *</Typography>
                  <TextField fullWidth size="small"       
                    value={submitData.email  ?? ''}
                    error={!!errors.email || stepOneValidation.email}
                    helperText={errors.email || stepOneValidation.email}
                    disabled={loadingStatus.isLoading }
                    onChange={(e) => { setSubmitData((prevData) => ({ ...prevData, email: e.target.value }))}}
                    sx={{ ...styled('autocomplete',"input", "filled") }}         
                    placeholder="mail@mail.com" 
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} >
              <Grid container item spacing={3}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Полное имя *</Typography>
                  <TextField fullWidth size="small"       
                    value={submitData.fio ?? ''}
                    error={!!errors.fio || stepOneValidation.fio}
                    helperText={errors.fio || stepOneValidation.fio}
                    disabled={loadingStatus.isLoading}
                    onChange={(e) => { setSubmitData((prevData) => ({ ...prevData, fio: e.target.value }))}}
                    sx={{ ...styled('autocomplete',"input", "filled") }}         
                    placeholder="Иванов Иван Иванович" 
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} pt={1}>
              <Grid container item spacing={3}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} lineHeight={1.45} sx={{ color: theme.palette.grey.main }}>
                    Нажимая кнопку <b>«Зарегистрироваться»</b>, вы принимаете условия {' '}
                    <Link underline='none' target='_blank' href="https://cdn.ds24.ru/fs/3/1750840620_8qd2v.html" sx={{ color: theme.palette.blue, borderColor: theme.palette.blue, borderBottom: '1px solid', }}>
                      Договора-оферты
                    </Link>,{' '}
                    и даете согласие на обработку персональных данных.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            </>
          )}  

          {/* Регистрация УК */}
          {submitData.step == 2 && (
            <>
            <Grid item xs={12} >
              <Grid container item spacing={3}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Название компании *</Typography>
                  <TextField fullWidth size="small"       
                    value={submitData.cnt_name  ?? ''}
                    error={!!errors.cnt_name}
                    helperText={errors.cnt_name}
                    disabled={loadingStatus.isLoading}
                    onChange={(e) => { setSubmitData((prevData) => ({ ...prevData, cnt_name: e.target.value }))}}
                    sx={{ ...styled('autocomplete',"input", "filled") }}         
                    placeholder="Например, ООО «Моя компания»" 
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} >
              <Grid container item spacing={3}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>ИНН *</Typography>
                  <TextField fullWidth size="small"       
                    value={submitData.inn  ?? ''}
                    error={!!errors.inn}
                    helperText={errors.inn}
                    inputProps={{ maxLength: 12 }}
                    disabled={loadingStatus.isLoading}
                    onChange={(e) => { setSubmitData((prevData) => ({ ...prevData, inn: e.target.value }))}}
                    sx={{ ...styled('autocomplete',"input", "filled") }}         
                    placeholder="012345678910" 
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} >
              <Grid container item spacing={3}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Регион *</Typography>
                  <Autocomplete size="small" fullWidth
                    getOptionLabel={(option) => option.region_name}
                    value={ regions.find((item) => item.code === submitData.region_code) || null }
                    options={regions}
                    disabled={loadingStatus.isLoading}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    onChange={(e, newValue) => { 
                      setSubmitData((prevData) => ({ 
                        ...prevData, 
                        region_code: newValue?.code ?? null, 
                        region_name: newValue?.region_name ?? null 
                      }))}
                    }
                    renderOption={(props, option) => (
                      <li {...props} style={{ minHeight: 40, padding: '8px 16px' }}>
                        {option.region_name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField 
                        placeholder="Например, Москва"
                        error={!!errors.region_code}
                        helperText={errors.region_code}
                        {...params} 
                        sx={{
                          ...styled('autocomplete', 'input', 'filled'),
                          '& .MuiAutocomplete-inputRoot': { padding: '5.5px !important' },
                          '& .MuiAutocomplete-input': { padding: '5.5px !important' },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} pt={1}>
              <Grid container item spacing={3}>
                <Grid xs={12} item display='flex'  gap={1.5}>
                  <InfoOutlineIcon sx={{ color: theme.palette.grey.main, cursor: 'default' }} />
                  <Typography fontSize={14} lineHeight={1.35} sx={{ color: theme.palette.grey.main }}>
                    Логин и пароль для входа в личный кабинет будут направлены на вашу электронную почту
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            </>
          )} 
        </Grid>

        <Grid item display='flex' alignItems='center' justifyContent='space-between' gap={1.5} pt={4} pb={6}>
          {submitData.step == 1 && (
            <Button 
              disabled={(loadingStatus.isLoading || !(submitData.fio && submitData.phone && submitData.email))} 
              sx={{ textTransform: 'none', paddingX: '30px', paddingY: '12px' }} 
              disableElevation 
              variant="contained" 
              color={"green"} 
              onClick={validateStepOne} 
              type="button"
            >
              {loadingStatus.isLoading && (<Box  sx={{ alignItems: 'center', display: 'flex' }} pr={1}><CircularProgress size={15} color='inherit' /></Box>)}
              Далее
            </Button>
          )}

          {submitData.step == 2 && (
            <>
            {/* <Button sx={{ textTransform: 'none' }} disabled={loadingStatus.isLoading} disableElevation variant="outlined" color={"grey"} onClick={() => setSubmitData((prevData) => ({ ...prevData, step: 1 }))} type="button">
              Назад
            </Button> */}
            <Button 
              disabled={(loadingStatus.isLoading || !(submitData.cnt_name && submitData.inn && submitData.region_code))} 
              sx={{ textTransform: 'none', paddingX: '30px', paddingY: '12px' }} 
              disableElevation 
              variant="contained" 
              color={"green"} 
              onClick={submit} 
              type="submit"
            >
              {loadingStatus.isLoading && (<Box  sx={{ alignItems: 'center', display: 'flex' }} pr={1}><CircularProgress size={15} color='inherit' /></Box>)}
              Начать работу
            </Button>
            </>
          )}

          <Grid sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 500, fontSize: 16, color: theme.palette.grey.main }}>Шаг {submitData.step} из 2</Typography>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress variant="determinate" value={100} size={24} thickness={4} sx={{ color: theme.palette.grey[300], zIndex: 1 }}  />
              <CircularProgress
                variant="determinate"
                value={submitData.step === 2 ? 100 : -50}
                size={24}
                thickness={4}
                color="success"
                sx={{ position: "absolute", left: 0, zIndex: 2 }}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <AskChatIcon style={{ cursor: 'default' }} />
          <Typography sx={{ color: theme.palette.grey.main, fontSize: 14 }}>
            Техническая поддержка:{' '}
            <Link 
              href="mailto:sales@ds24.ru" 
              underline="none" 
              sx={{ color: theme.palette.grey.main, borderColor: theme.palette.grey.main, borderBottom: '1px solid', }}
            >
              sales@ds24.ru
            </Link>
          </Typography>
        </Grid>

      </Card>
      <Snackbar
        open={loadingStatus.error}
        autoHideDuration={100}
        onClose={() => {}}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity='error'>{loadingStatus.errorMessage || 'Произошла ошибка'}</Alert>
      </Snackbar>
    </Box>
  )
}

export default Register

const Card = styles(Grid)(({ theme }) => ({
  // boxShadow: '0 2px 8px rgba(0, 0, 12, 0.1)',
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: 16,
  maxWidth: '32rem',
}));
